import * as upApiClient from '@odiupsk/up-api-client'
import { action, computed, observable } from 'mobx'
import { SCOPES } from '../config/constants'

import { Auth } from './modules/auth/Auth'
import { maxPerPage } from './utils/maxPerPage'
import { usersApi } from './api'

const LAST_ENTITY_ID_LOCAL_STORAGE_KEY = 'context.lastEntityId' // what client/end-user id was last active, so that we can continue next time

export class AppStore {
  @observable currentUser?: upApiClient.User = undefined

  @observable currentUserLoading = true

  @observable currentUserAccesses: upApiClient.UserAccess[] = []

  @observable currentRole: SCOPES = SCOPES.Public

  @observable isLoggedIn?: boolean

  @observable isMigratedUser?: boolean

  @observable isMigratedAdmin = false

  @observable showMigrationModal = false

  @computed get isAdmin() {
    return this.currentUserAccesses.some(access => access.master)
  }

  @computed get isCurrentAccessAdmin() {
    const currentAccessId = this.getIdInScope(this.currentRole)
    return (
      this.currentUserAccesses.find(access => access.scope_object_id === currentAccessId)?.master ??
      false
    )
  }

  @computed get isRestrictedByMigration() {
    return this.isMigratedUser /* && !this.isAdmin */
  }

  @observable is404 = false

  @action async fetchLoggedInUser(forceUpdate = false) {
    if (this.currentUser && !forceUpdate) {
      return
    }

    this.isMigratedUser = false
    this.isMigratedAdmin = false

    const userId = Auth.getUserId()
    const userToken = Auth.getAccessToken()

    if (!userId) {
      throw new Error('userId missing')
    }

    this.currentUserLoading = true

    const userResponse = await usersApi.getUser(userId)

    if (!userResponse.data) return
    this.currentUser = userResponse.data
    this.currentUserLoading = false

    if (!Auth.forceChangeEmail(userToken!)) {
      const userAccesses = await usersApi.getUserAccesses(userId, {
        params: { per_page: maxPerPage },
      })
      this.currentUserAccesses = userAccesses.data.data!
    }
  }

  reloadUser = async () => {
    return this.fetchLoggedInUser(true)
  }

  saveLastEntityIdToLocalStorage = (id: string) => {
    localStorage.setItem(LAST_ENTITY_ID_LOCAL_STORAGE_KEY, id)
  }

  getLastEntityIdFromLocalStorage = () => {
    return localStorage.getItem(LAST_ENTITY_ID_LOCAL_STORAGE_KEY)
  }

  getIdInScope(scope: SCOPES) {
    const access = this.currentUserAccesses.find(i => i.scope === scope)
    return access && access.scope_object_id
  }

  hasIdInSomeScope(scopes: SCOPES[]) {
    const access = this.currentUserAccesses.some(i => scopes.includes(i.scope))
    return access
  }

  hasIdInAccesses(id: string) {
    return this.currentUserAccesses.some(access => access.scope_object_id === id)
  }

  getScopeFromId(id: string) {
    const userAccess = this.currentUserAccesses.find(access => access.scope_object_id === id)

    if (userAccess) {
      return userAccess.scope
    }
  }

  getUserAccessFromId(id: string) {
    return this.currentUserAccesses.find(access => access.scope_object_id === id)
  }

  @action checkMigrationForEntity(entityId: string, scope: SCOPES) {
    const access = this.currentUserAccesses.find(a => a.scope_object_id === entityId)
    if (!access) return

    if (scope === SCOPES.Holder && access.is_migrated) {
      this.isMigratedUser = true
      this.showMigrationModal = true
    } else if (scope === SCOPES.Client && access.can_manage_products?.some(p => p.is_migrated)) {
      this.isMigratedUser = true
      this.isMigratedAdmin = true
      this.showMigrationModal = true
    }
  }

  @action restrictByMigration(): boolean {
    if (this.isRestrictedByMigration) {
      this.showMigrationModal = true
      return true
    }
    return false
  }

  @action dismissMigrationModal() {
    this.showMigrationModal = false
  }

  getIsUserMasterFromId(id: string): boolean | undefined {
    const userAccess = this.currentUserAccesses.find(access => access.scope_object_id === id)
    return userAccess && userAccess.master
  }
}

export const appStore = new AppStore()
